import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";
import PoolInfoCard from "./PoolInfoCard";
import ClientsCard from "./ClientsCard";
import ManagersCard from "./ManagersCard";

const VALID_EDIT_SECTIONS = ["info", "clients", "managers"];

const ClientPoolDetailsPage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pool, setPool] = useState(null);
  const [clients, setClients] = useState([]);
  const [managers, setManagers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const editParam = searchParams.get("edit");
  const editingSection = VALID_EDIT_SECTIONS.includes(editParam) ? editParam : null;

  const setEditingSection = (section) => {
    const next = new URLSearchParams(searchParams);
    if (section) {
      next.set("edit", section);
    } else {
      next.delete("edit");
    }
    setSearchParams(next, { replace: true });
  };

  const [form, setForm] = useState({
    client_ids: [],
    manager_ids: [],
  });
  const [infoForm, setInfoForm] = useState({
    name: "",
    description: "",
  });

  const fetchPoolDetails = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/client-pools/${id}`);
      const poolData = res.data.data;

      setPool(poolData);
      setForm({
        client_ids: poolData.clients?.map((c) => c.id) || [],
        manager_ids: poolData.managers?.map((m) => m.id) || [],
      });
    } catch (err) {
      console.error("Failed to fetch pool details:", err);
      setError("Failed to fetch pool details");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    try {
      const [clientsRes, managersRes] = await Promise.all([
        api.get("/clients"),
        api.get("/managers"),
      ]);
      setClients(clientsRes?.data?.data || []);
      setManagers(managersRes?.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch clients/managers:", err);
    }
  };

  useEffect(() => {
    fetchPoolDetails();
    fetchAllData();
  }, [id]);

  useEffect(() => {
    if (pool) {
      setInfoForm({
        name: pool.name || "",
        description: pool.description || "",
      });
    }
  }, [pool]);

  const handleMultiSelect = (field, value) => {
    setForm((prev) => {
      const exists = prev[field].includes(value);
      return {
        ...prev,
        [field]: exists
          ? prev[field].filter((i) => i !== value)
          : [...prev[field], value],
      };
    });
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;
  if (error)
    return <div className="text-center mt-10 text-red-600">{error}</div>;
  if (!pool) return <div className="text-center mt-10 text-gray-500">No pool found</div>;

  return (
    <>
      <div className="flex items-center gap-3 mb-6">
        <BackButton
          label={null}
          className="-ml-1.5"
          onClick={editingSection ? () => setEditingSection(null) : undefined}
        />
        <div className="h-5 w-px bg-gray-200" />
        <h1 className="text-xl font-bold text-gray-900">Client Pool</h1>
      </div>

      <div className="space-y-5">
        <PoolInfoCard
          pool={pool}
          isEditing={editingSection === "info"}
          setIsEditing={(v) => setEditingSection(v ? "info" : null)}
          infoForm={infoForm}
          setInfoForm={setInfoForm}
          setPool={setPool}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ClientsCard
            pool={pool}
            allClients={clients}
            isEditing={editingSection === "clients"}
            setIsEditing={(v) => setEditingSection(v ? "clients" : null)}
            formState={form}
            handleMultiSelect={handleMultiSelect}
            setPool={setPool}
          />

          <ManagersCard
            pool={pool}
            allManagers={managers}
            isEditing={editingSection === "managers"}
            setIsEditing={(v) => setEditingSection(v ? "managers" : null)}
            formState={form}
            setFormState={setForm}
            handleMultiSelect={handleMultiSelect}
            setPool={setPool}
          />
        </div>
      </div>
    </>
  );
};

export default ClientPoolDetailsPage;
